import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Brain, ArrowLeft, Shield, TrendingUp, Users, DollarSign, Activity, Loader2, Globe, Gift, Share2, Copy, Check, X as XIcon, BarChart3, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import AnalyticsTabs from '@/components/AnalyticsTabs';
import UserAnalysesList from '@/components/UserAnalysesList';
import FeedbackTab from '@/components/FeedbackTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [analyses, setAnalyses] = useState([]);

  // Promo codes state
  const [promoCodes, setPromoCodes] = useState([]);
  const [promoLoading, setPromoLoading] = useState(false);
  const [newPromoCredits, setNewPromoCredits] = useState(1000);
  const [newPromoCount, setNewPromoCount] = useState(5);

  // Referral stats state
  const [referralStats, setReferralStats] = useState(null);
  const [referralLoading, setReferralLoading] = useState(false);

  useEffect(() => {
    const adminId = localStorage.getItem('adminId');
    if (adminId) {
      setIsAuthenticated(true);
      loadAdminData();
    }
  }, []);

  const handleLogin = async () => {
    if (!email || !password) {
      toast.error('Please enter credentials');
      return;
    }

    setLoading(true);
    try {
      // Use regular login to get JWT token
      const loginResponse = await axios.post(`${API}/auth/login`, { email, password });
      
      if (loginResponse.data.access_token) {
        const { access_token, user } = loginResponse.data;
        
        // Check if user is admin
        if (!user.is_admin) {
          toast.error('Admin access required');
          setLoading(false);
          return;
        }
        
        // Store tokens
        localStorage.setItem('authToken', access_token);
        localStorage.setItem('adminId', user.id);
        localStorage.setItem('userId', user.id);
        
        setIsAuthenticated(true);
        toast.success('Admin login successful');
        await loadAdminData();
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Invalid credentials or not an admin account');
    } finally {
      setLoading(false);
    }
  };

  const loadAdminData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [statsRes, usersRes, analysesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/admin/analyses`, { headers })
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users || []);
      setAnalyses(analysesRes.data.analyses || []);
      
      // Load promo codes and referral stats
      await loadPromoCodes();
      await loadReferralStats();
    } catch (error) {
      console.error('Failed to load admin data:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        handleLogout();
      }
    }
  };

  const loadPromoCodes = async () => {
    setPromoLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API}/admin/promo-codes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPromoCodes(response.data.codes || []);
    } catch (error) {
      console.error('Failed to load promo codes:', error);
      toast.error('Failed to load promo codes');
    } finally {
      setPromoLoading(false);
    }
  };

  const loadReferralStats = async () => {
    setReferralLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API}/admin/referral-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferralStats(response.data);
    } catch (error) {
      console.error('Failed to load referral stats:', error);
    } finally {
      setReferralLoading(false);
    }
  };

  const handleCreatePromoCodes = async () => {
    if (!newPromoCredits || newPromoCredits < 1) {
      toast.error('Credits must be at least 1');
      return;
    }
    if (!newPromoCount || newPromoCount < 1 || newPromoCount > 100) {
      toast.error('Count must be between 1 and 100');
      return;
    }

    setPromoLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(
        `${API}/admin/create-promo-codes`,
        {
          credits: parseInt(newPromoCredits),
          count: parseInt(newPromoCount),
          expires_in_days: null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      await loadPromoCodes();
      setNewPromoCredits(1000);
      setNewPromoCount(5);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create promo codes');
    } finally {
      setPromoLoading(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied!');
  };

  const handleLogout = () => {
    localStorage.removeItem('adminId');
    setIsAuthenticated(false);
    navigate('/');
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-6 h-6 text-indigo-600" />
              <CardTitle>Admin Login</CardTitle>
            </div>
            <CardDescription>Enter your admin credentials</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                placeholder="admin@sentisight.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
              />
            </div>
            <div>
              <Label>Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
              />
            </div>
            <Button
              onClick={handleLogin}
              disabled={loading}
              className="w-full bg-indigo-600 hover:bg-indigo-700"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Login'}
            </Button>
            <Button variant="ghost" onClick={() => navigate('/')} className="w-full">
              Back to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-gray-200 bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-2 md:py-3 flex justify-between items-center">
          {/* Logo & Title - Left, Clickable */}
          <div 
            className="flex items-center gap-2 md:gap-3 cursor-pointer"
            onClick={() => navigate('/')}
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_credit-system-12/artifacts/3r5buf5n_Emo.zip%20-%204.png"
              alt="EmoQ"
              className="h-14 md:h-16 w-auto"
            />
            <div className="hidden sm:flex items-center gap-2">
              <Shield className="w-5 h-5 text-indigo-600" />
              <span className="font-bold text-sm md:text-lg" style={{fontFamily: 'Space Grotesk'}}>Admin Dashboard</span>
            </div>
          </div>
          
          {/* Logout Button - Right */}
          <Button onClick={handleLogout} variant="outline" size="sm">Logout</Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {!stats ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mx-auto" />
          </div>
        ) : (
          <div className="space-y-8">
            {/* Stats Cards */}
            <div className="grid md:grid-cols-4 gap-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Total Users</CardTitle>
                  <Users className="w-4 h-4 text-gray-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{stats.total_users}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Total Analyses</CardTitle>
                  <Activity className="w-4 h-4 text-gray-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{stats.total_analyses}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Total Revenue</CardTitle>
                  <DollarSign className="w-4 h-4 text-gray-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">${stats.total_revenue?.toFixed(2) || '0.00'}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Active Subscriptions</CardTitle>
                  <TrendingUp className="w-4 h-4 text-purple-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{stats.active_subscriptions || 0}</div>
                  <p className="text-xs text-gray-500 mt-1">Recurring monthly</p>
                </CardContent>
              </Card>
            </div>

            {/* Second Row - Additional Stats */}
            <div className="grid md:grid-cols-4 gap-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">API Integrations</CardTitle>
                  <BarChart3 className="w-4 h-4 text-gray-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{stats.api_integrations || 0}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">New Users (7d)</CardTitle>
                  <Users className="w-4 h-4 text-green-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">
                    {users.filter(u => {
                      const created = new Date(u.created_at);
                      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
                      return created > weekAgo;
                    }).length}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Analyses (24h)</CardTitle>
                  <Activity className="w-4 h-4 text-blue-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">
                    {analyses.filter(a => {
                      const timestamp = new Date(a.timestamp);
                      const dayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
                      return timestamp > dayAgo;
                    }).length}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Total Credits Issued</CardTitle>
                  <DollarSign className="w-4 h-4 text-yellow-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">
                    {users.reduce((sum, u) => sum + (u.credits || 0), 0).toLocaleString()}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Tabs */}
            <Tabs defaultValue="analytics" className="mt-6">
              <TabsList>
                <TabsTrigger value="analytics">Analytics</TabsTrigger>
                <TabsTrigger value="map">Global Heatmap</TabsTrigger>
                <TabsTrigger value="promo">Promo Codes</TabsTrigger>
                <TabsTrigger value="referrals">Referrals</TabsTrigger>
                <TabsTrigger value="users">Users</TabsTrigger>
                <TabsTrigger value="analyses">All Analyses</TabsTrigger>
                <TabsTrigger value="feedback">Ratings & Feedback</TabsTrigger>
                <TabsTrigger value="api">API Users</TabsTrigger>
              </TabsList>

              {/* New Analytics Tab */}
              <TabsContent value="analytics" className="mt-6">
                <AnalyticsTabs analyses={analyses} darkMode={false} />
              </TabsContent>

              <TabsContent value="map" className="mt-6">
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <Globe className="w-5 h-5 text-indigo-600" />
                      <CardTitle>Global Emotional Intelligence Map</CardTitle>
                    </div>
                    <CardDescription>
                      Real-time heatmap of emotional analysis across countries. Colors represent emotional valence (positivity).
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="p-8 text-center text-gray-500">
                      <Globe className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                      <p>Geographic visualization temporarily unavailable</p>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Promo Codes Tab */}
              <TabsContent value="promo" className="mt-6 space-y-6">
                {/* Create New Codes */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <Gift className="w-5 h-5 text-purple-600" />
                      <CardTitle>Create Promo Codes</CardTitle>
                    </div>
                    <CardDescription>Generate new single-use promo codes for credit top-ups</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label>Credits per Code</Label>
                        <Input
                          type="number"
                          min="1"
                          value={newPromoCredits}
                          onChange={(e) => setNewPromoCredits(e.target.value)}
                          placeholder="1000"
                        />
                      </div>
                      <div>
                        <Label>Number of Codes</Label>
                        <Input
                          type="number"
                          min="1"
                          max="100"
                          value={newPromoCount}
                          onChange={(e) => setNewPromoCount(e.target.value)}
                          placeholder="5"
                        />
                      </div>
                      <div className="flex items-end">
                        <Button
                          onClick={handleCreatePromoCodes}
                          disabled={promoLoading}
                          className="w-full bg-gradient-to-r from-purple-600 to-indigo-600"
                        >
                          {promoLoading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Gift className="w-4 h-4 mr-2" />
                              Generate Codes
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-500 mt-4">
                      💡 Codes are auto-generated, single-use only, and never expire
                    </p>
                  </CardContent>
                </Card>

                {/* All Promo Codes */}
                <Card>
                  <CardHeader>
                    <CardTitle>All Promo Codes ({promoCodes.length})</CardTitle>
                    <CardDescription>Manage and track promo code usage</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {promoLoading ? (
                      <div className="flex justify-center py-8">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                      </div>
                    ) : promoCodes.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Gift className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                        <p>No promo codes yet. Create your first batch above!</p>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {promoCodes.map((code, idx) => (
                          <div
                            key={idx}
                            className={`flex items-center justify-between p-4 rounded-lg border ${
                              code.is_used 
                                ? 'bg-gray-50 border-gray-200' 
                                : 'bg-green-50 border-green-200'
                            }`}
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-3">
                                <code className="text-lg font-bold font-mono">
                                  {code.code}
                                </code>
                                <Badge variant={code.is_used ? 'secondary' : 'success'}>
                                  {code.is_used ? 'Used' : 'Available'}
                                </Badge>
                                <Badge variant="outline">{code.credits} credits</Badge>
                              </div>
                              {code.is_used && (
                                <p className="text-sm text-gray-600 mt-1">
                                  Used by: {code.used_by_email || 'Unknown'} on{' '}
                                  {new Date(code.used_at).toLocaleString()}
                                </p>
                              )}
                              <p className="text-xs text-gray-500 mt-1">
                                Created: {new Date(code.created_at).toLocaleString()}
                              </p>
                            </div>
                            {!code.is_used && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => copyCode(code.code)}
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Referrals Tab */}
              <TabsContent value="referrals" className="mt-6">
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <Share2 className="w-5 h-5 text-green-600" />
                      <CardTitle>Referral Program Analytics</CardTitle>
                    </div>
                    <CardDescription>Track referral performance and user acquisition</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {referralLoading ? (
                      <div className="flex justify-center py-8">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                      </div>
                    ) : referralStats ? (
                      <div className="space-y-6">
                        {/* Summary Stats */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="p-6 bg-blue-50 rounded-lg text-center">
                            <div className="text-3xl font-bold text-blue-600">
                              {referralStats.summary?.total_referrers || 0}
                            </div>
                            <div className="text-sm text-gray-600 mt-1">Active Referrers</div>
                          </div>
                          <div className="p-6 bg-green-50 rounded-lg text-center">
                            <div className="text-3xl font-bold text-green-600">
                              {referralStats.summary?.total_referrals || 0}
                            </div>
                            <div className="text-sm text-gray-600 mt-1">Total Referrals</div>
                          </div>
                          <div className="p-6 bg-purple-50 rounded-lg text-center">
                            <div className="text-3xl font-bold text-purple-600">
                              {referralStats.summary?.total_credits_given || 0}
                            </div>
                            <div className="text-sm text-gray-600 mt-1">Credits Distributed</div>
                          </div>
                        </div>

                        {/* Top Referrers */}
                        <div>
                          <h3 className="text-lg font-semibold mb-4">Top Referrers</h3>
                          <div className="space-y-2 max-h-96 overflow-y-auto">
                            {referralStats.stats && referralStats.stats.length > 0 ? (
                              referralStats.stats.slice(0, 20).map((stat, idx) => (
                                <div
                                  key={idx}
                                  className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-white rounded-lg border"
                                >
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <Badge variant="outline">#{idx + 1}</Badge>
                                      <code className="font-bold text-blue-600">
                                        {stat.referral_code}
                                      </code>
                                    </div>
                                    <p className="text-sm text-gray-600 mt-1">{stat.user_email}</p>
                                  </div>
                                  <div className="text-right">
                                    <div className="text-xl font-bold text-green-600">
                                      {stat.total_referrals}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                      +{stat.total_credits_earned}⚡
                                    </div>
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="text-center py-8 text-gray-500">
                                <Share2 className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                                <p>No referrals yet</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-center text-gray-500">Unable to load referral stats</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="users" className="mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle>All Users ({users.length})</CardTitle>
                    <CardDescription>Complete user list with credits and activity</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-3 text-sm font-medium text-gray-600">Email</th>
                            <th className="text-left p-3 text-sm font-medium text-gray-600">Credits</th>
                            <th className="text-left p-3 text-sm font-medium text-gray-600">Role</th>
                            <th className="text-left p-3 text-sm font-medium text-gray-600">Created</th>
                            <th className="text-left p-3 text-sm font-medium text-gray-600">Subscription</th>
                          </tr>
                        </thead>
                        <tbody>
                          {users.map((user) => (
                            <tr key={user.id} className="border-b hover:bg-gray-50">
                              <td className="p-3">
                                <div>
                                  <p className="font-medium">{user.email || 'Anonymous'}</p>
                                  <p className="text-xs text-gray-500">ID: {user.id?.slice(0, 12)}...</p>
                                </div>
                              </td>
                              <td className="p-3">
                                <Badge variant="outline" className="font-mono">
                                  {user.credits || 0} 
                                </Badge>
                              </td>
                              <td className="p-3">
                                <Badge variant={user.is_admin ? 'default' : 'secondary'}>
                                  {user.role || (user.is_admin ? 'admin' : 'user')}
                                </Badge>
                              </td>
                              <td className="p-3 text-sm text-gray-600">
                                {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                              </td>
                              <td className="p-3">
                                {user.subscription_status === 'active' ? (
                                  <Badge className="bg-green-500">Active</Badge>
                                ) : (
                                  <span className="text-xs text-gray-400">None</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="analyses" className="mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle>All User Analyses</CardTitle>
                    <CardDescription>View all analyses with input content and detailed results</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <UserAnalysesList analyses={analyses} darkMode={false} />
                  </CardContent>
                </Card>
              </TabsContent>


              <TabsContent value="feedback" className="mt-6">
                <FeedbackTab />
              </TabsContent>

              <TabsContent value="api" className="mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle>API Integrations</CardTitle>
                    <CardDescription>Businesses using the API</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {stats.api_users.length === 0 ? (
                      <p className="text-gray-500 text-sm">No API users yet</p>
                    ) : (
                      <div className="space-y-4">
                        {stats.api_users.map((user) => (
                          <div key={user.id} className="p-4 border rounded-lg">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-semibold">{user.organization_name || user.email}</p>
                                <p className="text-xs text-gray-500 mt-1">API Key: {user.api_key?.slice(0, 20)}...</p>
                              </div>
                              <Badge>{user.api_tier || 'N/A'}</Badge>
                            </div>
                            <div className="mt-2 text-xs text-gray-500">
                              <span>Calls: {user.api_calls || 0}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;